import React, { useState, useEffect } from 'react';
import {
    FiMoon,
    FiSun,
    FiTrash2,
    FiLock,
    FiCheck,
    FiX
} from 'react-icons/fi';
import '../Styles/Settings.css';
import { applyTheme } from './theme';

const Settings = ({
    onClose,
    chatHistory,
    setChatHistory,
    deletedChats,
    setDeletedChats,
    setActiveChatId,
    setMessages,
    setShowDashboard
}) => {
    const [activeTab, setActiveTab] = useState('theme');
    const [currentTheme, setCurrentTheme] = useState(
        localStorage.getItem('theme') || 'light'
    );
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [passwordData, setPasswordData] = useState({
        current: '',
        new: '',
        confirm: ''
    });
    const [passwordErrors, setPasswordErrors] = useState({});

    // Theme switching function
    const changeTheme = (theme) => {
        setCurrentTheme(theme);
        localStorage.setItem('theme', theme);
        applyTheme(theme);
    };

    // Delete all chats function
    const handleDeleteAllChats = () => {
        setDeletedChats([...deletedChats, ...chatHistory]);
        setChatHistory([]);
        localStorage.setItem('chatHistory', JSON.stringify([]));
        localStorage.setItem('deletedChats', JSON.stringify([...deletedChats, ...chatHistory]));
        setShowDeleteConfirm(false);

        // Clear active chat and dashboard
        if (setActiveChatId) setActiveChatId(null);
        if (setMessages) setMessages([]);
        if (setShowDashboard) setShowDashboard(false);
    };

    // Password reset function
    const handlePasswordReset = (e) => {
        e.preventDefault();
        const errors = {};

        if (!passwordData.current) errors.current = 'Current password is required';
        if (!passwordData.new) errors.new = 'New password is required';
        else if (passwordData.new.length < 8) errors.new = 'Password must be at least 8 characters';
        if (passwordData.new !== passwordData.confirm) errors.confirm = 'Passwords do not match';

        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }

        // Here you would typically call an API to change the password
        alert('Password changed successfully!');
        setPasswordData({ current: '', new: '', confirm: '' });
        setPasswordErrors({});
    };

    // Apply theme on component mount
    useEffect(() => {
        applyTheme(currentTheme);
    }, [currentTheme]);

    return (
        <div className="settings-modal-overlay">
            <div className="settings-modal">
                <div className="settings-header">
                    <h2>Settings</h2>
                    <button className="close-btn" onClick={onClose}>
                        <FiX size={20} />
                    </button>
                </div>

                <div className="settings-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        Profile
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'theme' ? 'active' : ''}`}
                        onClick={() => setActiveTab('theme')}
                    >
                        Theme
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'bin' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bin')}
                    >
                        Bin
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
                        onClick={() => setActiveTab('password')}
                    >
                        Password
                    </button>
                </div>

                <div className="settings-content">
                    {activeTab === 'profile' && (
                        <div className="profile-options">
                            <h3>Account Information</h3>
                            <div className="profile-info">
                                <div className="info-row">
                                    <label>Account Type:</label>
                                    <span className="info-value">
                                        {localStorage.getItem('userType') === 'vendor' ? 'Vendor Portal' : 'SaviAI Portal'}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <label>Email:</label>
                                    <span className="info-value">
                                        {localStorage.getItem('userType') === 'vendor' 
                                            ? localStorage.getItem('userEmail') 
                                            : localStorage.getItem('user')}
                                    </span>
                                </div>
                                {localStorage.getItem('authMethod') && (
                                    <div className="info-row">
                                        <label>Login Method:</label>
                                        <span className="info-value">
                                            {localStorage.getItem('authMethod') === 'microsoft' ? 'Microsoft Account' : 'Email & Password'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'theme' && (
                        <div className="theme-options">
                            <h3>Appearance</h3>
                            <div className="theme-option" onClick={() => changeTheme('light')}>
                                <div className={`theme-preview light ${currentTheme === 'light' ? 'active' : ''}`}>
                                    <FiSun size={24} />
                                </div>
                                <span>Light Mode</span>
                                {currentTheme === 'light' && <FiCheck className="check-icon" />}
                            </div>
                            <div className="theme-option" onClick={() => changeTheme('dark')}>
                                <div className={`theme-preview dark ${currentTheme === 'dark' ? 'active' : ''}`}>
                                    <FiMoon size={24} />
                                </div>
                                <span>Dark Mode</span>
                                {currentTheme === 'dark' && <FiCheck className="check-icon" />}
                            </div>
                        </div>
                    )}

                    {activeTab === 'bin' && (
                        <div className="bin-options">
                            <h3>Chat Bin Management</h3>
                            <p>You have {chatHistory.length} active chats</p>
                            <button
                                className="delete-btn"
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                <FiTrash2 size={16} />
                                Delete All Chats
                            </button>

                            {showDeleteConfirm && (
                                <div className="confirm-dialog">
                                    <p>Are you sure you want to delete all chats?</p>
                                    <div className="confirm-actions">
                                        <button
                                            className="confirm-btn danger"
                                            onClick={handleDeleteAllChats}
                                        >
                                            Delete All
                                        </button>
                                        <button
                                            className="confirm-btn"
                                            onClick={() => setShowDeleteConfirm(false)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'password' && (
                        <div className="password-options">
                            <h3>Reset Password</h3>
                            <form onSubmit={handlePasswordReset}>
                                <div className="form-group">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        value={passwordData.current}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            current: e.target.value
                                        })}
                                    />
                                    {passwordErrors.current && (
                                        <span className="error">{passwordErrors.current}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        value={passwordData.new}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            new: e.target.value
                                        })}
                                    />
                                    {passwordErrors.new && (
                                        <span className="error">{passwordErrors.new}</span>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        value={passwordData.confirm}
                                        onChange={(e) => setPasswordData({
                                            ...passwordData,
                                            confirm: e.target.value
                                        })}
                                    />
                                    {passwordErrors.confirm && (
                                        <span className="error">{passwordErrors.confirm}</span>
                                    )}
                                </div>
                                <button type="submit" className="save-btn">
                                    <FiLock size={16} />
                                    Change Password
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;